import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EnquiryModal({ open, onOpenChange, initialData = {} }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    workspace_type: '',
    company_name: '',
    seats_required: '',
    message: '',
    ...initialData,
  });

  const [errors, setErrors] = useState({});

  const locations = ['Sarjapur', 'Hebbal', 'Others'];
  const workspaceTypes = [
    'Coworking Space',
    'Private Office',
    'Virtual Office',
    'Managed Enterprise',
    'Conference Room',
    'Day Pass',
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Phone must be 10 digits';
    }
    if (!formData.location) newErrors.location = 'Location is required';
    if (!formData.workspace_type) newErrors.workspace_type = 'Workspace type is required';
    if (!formData.seats_required) newErrors.seats_required = 'Seats required';
    else if (isNaN(formData.seats_required) || formData.seats_required <= 0) {
      newErrors.seats_required = 'Must be a positive number';
    }
    if (formData.message && formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);

    try {
      // Get UTM parameters from URL
      const urlParams = new URLSearchParams(window.location.search);
      const payload = {
        ...formData,
        seats_required: parseInt(formData.seats_required),
        form_type: initialData.form_type || 'general_contact',
        space_name: initialData.space_name || null,
        page_url: window.location.href,
        utm_source: urlParams.get('utm_source') || null,
        utm_medium: urlParams.get('utm_medium') || null,
        utm_campaign: urlParams.get('utm_campaign') || null,
        utm_content: urlParams.get('utm_content') || null,
      };

      await axios.post(`${API}/enquiry`, payload);
      
      toast.success('Thank you! We will get back to you within 24 hours.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        location: '',
        workspace_type: '',
        company_name: '',
        seats_required: '',
        message: '',
      });
      
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit enquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="enquiry-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Get In Touch</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                data-testid="input-name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                data-testid="input-email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                data-testid="input-phone"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="10 digit number"
                className={errors.phone ? 'border-red-500' : ''}
              />
              {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
            </div>

            <div>
              <Label htmlFor="location">Location *</Label>
              <Select value={formData.location} onValueChange={(val) => handleChange('location', val)}>
                <SelectTrigger data-testid="select-location" className={errors.location ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.location && <p className="text-red-500 text-xs mt-1">{errors.location}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="workspace_type">Workspace Type *</Label>
              <Select value={formData.workspace_type} onValueChange={(val) => handleChange('workspace_type', val)}>
                <SelectTrigger data-testid="select-workspace-type" className={errors.workspace_type ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {workspaceTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.workspace_type && <p className="text-red-500 text-xs mt-1">{errors.workspace_type}</p>}
            </div>

            <div>
              <Label htmlFor="seats_required">Seats Required *</Label>
              <Input
                id="seats_required"
                type="number"
                data-testid="input-seats"
                value={formData.seats_required}
                onChange={(e) => handleChange('seats_required', e.target.value)}
                min="1"
                className={errors.seats_required ? 'border-red-500' : ''}
              />
              {errors.seats_required && <p className="text-red-500 text-xs mt-1">{errors.seats_required}</p>}
            </div>
          </div>

          <div>
            <Label htmlFor="company_name">Company Name</Label>
            <Input
              id="company_name"
              data-testid="input-company"
              value={formData.company_name}
              onChange={(e) => handleChange('company_name', e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="message">Message / Requirements</Label>
            <Textarea
              id="message"
              data-testid="input-message"
              value={formData.message}
              onChange={(e) => handleChange('message', e.target.value)}
              rows={4}
              placeholder="Tell us about your requirements..."
              className={errors.message ? 'border-red-500' : ''}
            />
            {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="submit-enquiry-button"
              disabled={loading}
              className="bg-[#E12429] hover:bg-[#b81d21]"
            >
              {loading ? 'Submitting...' : 'Submit Enquiry'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}